import { Optional } from "common/utils/types";

import Alert from "client/common/components/content/Alert";
import { useFeatures } from "client/clinics/hooks";

import { useIsSubscriptionRefreshing } from "../hooks";
import { SubscriptionAddressIssue, SubscriptionPaymentMethod } from "../types";
import {
  useSubscriptionAddressUpdateDrawer,
  useSubscriptionClientInfoUpdateDrawer,
  useSubscriptionPaymentUpdateDrawer,
} from "../stackables";
import {
  getSubscriptionAddressIssueContent,
  getSubscriptionClientInfoForms,
  getSubscriptionPaymentIssueContent,
  isSubscriptionAddressUnshippable,
  isSubscriptionMissingClientInfo,
  isSubscriptionPaymentUnchargeable,
  SubscriptionClientInfoSubject,
  subscriptionClientInfoIssueContent,
} from "../utils";

type SubscriptionPaymentAlertProps = {
  subscription: {
    id: number;
    payment_method: Optional<SubscriptionPaymentMethod>;
  };
};

const SubscriptionPaymentAlert = ({ subscription }: SubscriptionPaymentAlertProps) => {
  const { open: openPaymentUpdate } = useSubscriptionPaymentUpdateDrawer();
  const { drawerMessage, actionLabel } = getSubscriptionPaymentIssueContent(
    subscription.payment_method,
  );

  // Stacked over the form it is blocking rather than routed to, so closing it returns the client
  // to the edit they came for.
  const onClick = () => {
    openPaymentUpdate({ id: subscription.id });
  };

  return (
    <Alert
      variant="danger"
      shouldScrollOnMount={true}
      actions={[{ label: actionLabel, onClick, hasRightIcon: true }]}
    >
      {drawerMessage}
    </Alert>
  );
};

type SubscriptionAddressAlertProps = {
  subscription: {
    id: number;
    shipping_address: Optional<SubscriptionAddressIssue>;
  };
};

const SubscriptionAddressAlert = ({ subscription }: SubscriptionAddressAlertProps) => {
  const { open: openAddressUpdate } = useSubscriptionAddressUpdateDrawer();
  const { drawerMessage, actionLabel } = getSubscriptionAddressIssueContent(
    subscription.shipping_address,
  );

  // Stacked over the form it is blocking rather than routed to, so closing it returns the client
  // to the edit they came for.
  const onClick = () => {
    openAddressUpdate({ id: subscription.id });
  };

  return (
    <Alert
      variant="danger"
      shouldScrollOnMount={true}
      actions={[{ label: actionLabel, onClick, hasRightIcon: true }]}
    >
      {drawerMessage}
    </Alert>
  );
};

type SubscriptionClientInfoAlertProps = {
  subscription: SubscriptionClientInfoSubject;
};

/** One action per account form, so a client missing several fields sees every one they need. */
const SubscriptionClientInfoAlert = ({ subscription }: SubscriptionClientInfoAlertProps) => {
  const { open: openClientInfoUpdate } = useSubscriptionClientInfoUpdateDrawer();
  const isGranularControlsEnabled = useFeatures().CONTROLLED_SUBSTANCE_GRANULAR_CONTROLS;
  const isRefreshing = useIsSubscriptionRefreshing(subscription.id);
  const { drawerMessage, actionLabels } = subscriptionClientInfoIssueContent;

  return (
    <Alert
      variant="danger"
      shouldScrollOnMount={true}
      areActionsLoading={isRefreshing}
      actions={getSubscriptionClientInfoForms(subscription, isGranularControlsEnabled).map(
        (form) => ({
          label: actionLabels[form],
          onClick: () => openClientInfoUpdate(form, subscription),
          hasRightIcon: true,
        }),
      )}
    >
      {drawerMessage}
    </Alert>
  );
};

type SubscriptionAlertProps = {
  subscription: SubscriptionClientInfoSubject & {
    id: number;
    payment_method: Optional<SubscriptionPaymentMethod>;
    shipping_address: Optional<SubscriptionAddressIssue>;
  };
};

const SubscriptionAlert = ({ subscription }: SubscriptionAlertProps) => {
  const isGranularControlsEnabled = useFeatures().CONTROLLED_SUBSTANCE_GRANULAR_CONTROLS;

  if (isSubscriptionPaymentUnchargeable(subscription)) {
    return <SubscriptionPaymentAlert subscription={subscription} />;
  }

  if (isSubscriptionAddressUnshippable(subscription)) {
    return <SubscriptionAddressAlert subscription={subscription} />;
  }

  if (isSubscriptionMissingClientInfo(subscription, isGranularControlsEnabled)) {
    return <SubscriptionClientInfoAlert subscription={subscription} />;
  }

  return null;
};

export default Object.assign(SubscriptionAlert, {
  Payment: SubscriptionPaymentAlert,
  Address: SubscriptionAddressAlert,
  ClientInfo: SubscriptionClientInfoAlert,
});
